/**
 * RouteErrorBoundary — catches render exceptions inside a route and shows a
 * friendly recovery panel instead of a white screen or Safari's "A problem
 * repeatedly occurred" auto-reload loop.
 *
 * Logs to console + (opt) POSTs /api/client-error for server-side monitoring.
 */
import React from 'react';

export default class RouteErrorBoundary extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // ChunkLoadError handling — a lazy() route chunk failed to load.  This
    // happens in two scenarios:
    //   1. The user is on a stale tab after a redeploy and the hashed chunk
    //      no longer exists at that URL.
    //   2. A flaky network / dev-server compile race returned text/html
    //      instead of JS for the chunk.
    // In both cases the right answer is to nuke the cache and reload once.
    // We track a sessionStorage flag so we don't infinite-loop on a
    // genuinely-broken bundle.
    const isChunkError =
      error?.name === 'ChunkLoadError' ||
      /Loading chunk \d+ failed/i.test(String(error?.message || '')) ||
      /Loading CSS chunk/i.test(String(error?.message || '')) ||
      /Refused to execute script/i.test(String(error?.message || ''));

    if (isChunkError) {
      try {
        const alreadyRetried = sessionStorage.getItem('cs_chunk_retry');
        if (!alreadyRetried) {
          sessionStorage.setItem('cs_chunk_retry', String(Date.now()));
          // Clear caches first so the reload picks up fresh chunks
          if ('caches' in window) {
            caches.keys().then(ks => Promise.all(ks.map(k => caches.delete(k))))
              .finally(() => window.location.reload());
          } else {
            window.location.reload();
          }
          return;
        }
        // Already retried this session — show the error UI, don't loop.
      } catch { /* noop */ }
    }

    // Surface to console for devs
    try {
      // eslint-disable-next-line no-console
      console.error('[RouteErrorBoundary]', this.props.routeLabel || 'route', error, info);
    } catch { /* noop */ }

    // Fire-and-forget report so we can pinpoint per-user issues
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      if (API && navigator.onLine !== false) {
        fetch(`${API}/api/client-error`, {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            route: this.props.routeLabel || window.location.pathname,
            message: String(error?.message || error),
            stack: String(error?.stack || '').slice(0, 4000),
            component_stack: String(info?.componentStack || '').slice(0, 4000),
            ua: navigator.userAgent || '',
            href: window.location.href,
          }),
        }).catch(() => {});
      }
    } catch { /* noop */ }
  }

  reset = () => this.setState({ hasError: false, error: null });

  hardReload = () => {
    try {
      // Unregister any service workers + clear caches, then reload —
      // recovers users who are stuck on a stale/broken bundle.
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(regs => {
          regs.forEach(r => r.unregister().catch(() => {}));
        }).catch(() => {});
      }
      if ('caches' in window) {
        caches.keys().then(ks => ks.forEach(k => caches.delete(k).catch(() => {}))).catch(() => {});
      }
    } catch { /* noop */ }
    setTimeout(() => window.location.reload(), 200);
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const { routeLabel = 'this page' } = this.props;
    return (
      <div
        className="max-w-md mx-auto my-10 p-6 rounded-2xl text-center"
        style={{
          background: 'var(--cs-bg-surface)',
          border: '1px solid rgba(205,4,11,0.35)',
          boxShadow: '0 24px 60px -16px rgba(205,4,11,0.25)',
        }}
        data-testid="route-error-boundary"
      >
        <div
          className="mx-auto mb-3 w-12 h-12 rounded-full flex items-center justify-center text-2xl"
          style={{ background: 'rgba(205,4,11,0.15)', color: '#CD040B' }}
          aria-hidden
        >
          !
        </div>
        <h2 className="text-lg font-black mb-1" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--cs-text)' }}>
          We hit a snag loading {routeLabel}
        </h2>
        <p className="text-sm mb-4" style={{ color: 'var(--cs-text-muted)' }}>
          A one-off glitch — try reloading below. If it keeps happening, the "Force reset" option clears any stuck browser cache.
        </p>
        <div className="flex gap-2 justify-center flex-wrap">
          <button
            onClick={this.reset}
            className="px-4 py-2 rounded-xl text-xs font-bold transition-all active:scale-95"
            style={{ background: '#CD040B', color: '#FFF' }}
            data-testid="route-error-retry"
          >
            Try again
          </button>
          <button
            onClick={this.hardReload}
            className="px-4 py-2 rounded-xl text-xs font-bold transition-all active:scale-95"
            style={{
              background: 'var(--cs-bg-hover)',
              color: 'var(--cs-text)',
              border: '1px solid var(--cs-border)',
            }}
            data-testid="route-error-force-reset"
          >
            Force reset
          </button>
        </div>
        {this.state.error?.message && (
          <pre
            className="mt-4 p-2 rounded-lg text-[10px] text-left overflow-auto"
            style={{ background: 'var(--cs-bg-hover)', color: 'var(--cs-text-muted)', maxHeight: 140 }}
          >
            {String(this.state.error.message).slice(0, 600)}
          </pre>
        )}
      </div>
    );
  }
}
